import { Accordion } from "@aws-amplify/ui-react";
import { Button, DialogContainer, Modal, ModalFooter, ModalTitle, ModalTitleContainer, ModelSubHeading } from "@factorearth/component-library";
import TemplateListSyncItem from "components/ProjectTab/SyncProject/TemplateSyncMenu/TemplateSyncListItem/TemplateListSyncItem";
import styles from "components/ProjectTab/SyncProject/TemplateSyncMenu/TemplateSyncMenu.module.scss";
import { FormTemplate, FormTemplateForResolver } from "db/definitions";
import { getAllFormTemplates as getAllOfflineFormTemplates } from "db/queries/formTemplateQueries";
import { Form } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { FiX } from "react-icons/fi";
import { useTheme } from "../../hooks/useSingleton";
interface SelectAutoSyncTemplatesProps {
	formTemplates: FormTemplateForResolver[];
	isSubmitting?: boolean;
	handleClose: () => void;
	openSelectFormTemplates: boolean;
	setOpenSelectFormTemplates: React.Dispatch<React.SetStateAction<boolean>>;
	selectedFormTemplateIds: string[];
	setDeselectedFormTemplateIds?: React.Dispatch<React.SetStateAction<string[]>>;
	setSelectedFormTemplateIds: React.Dispatch<React.SetStateAction<string[]>>;
	editSubmit?: () => void;
}

export const SelectAutoSyncTemplates = (props: SelectAutoSyncTemplatesProps) => {
	const {
		formTemplates,
		isSubmitting,
		setOpenSelectFormTemplates,
		openSelectFormTemplates,
		handleClose,
		selectedFormTemplateIds,
		setSelectedFormTemplateIds,
		editSubmit,
		setDeselectedFormTemplateIds,
	} = props;
	const colorPalette = useTheme();
	const [offlineTemplates, setOfflineTemplates] = useState<FormTemplateForResolver[]>([]);

	useEffect(() => {
		const setUpOfflineTemplates = async () => {
			const offlineData = await getAllOfflineFormTemplates();
			setOfflineTemplates(offlineData);
		};
		setUpOfflineTemplates();
	}, []);

	function handleSelect(selectedTemplate: FormTemplate) {
		const selectedCopy = [...selectedFormTemplateIds];
		const index = selectedCopy.findIndex((id) => id === selectedTemplate.id);

		if (index === -1) {
			selectedCopy.push(selectedTemplate.id);
		} else {
			if (setDeselectedFormTemplateIds) {
				setDeselectedFormTemplateIds((prev) => {
					const index = prev.findIndex((id) => id === selectedTemplate.id);
					if (index !== -1) {
						return prev;
					} else {
						return [...prev, selectedTemplate.id];
					}
				});
			}
			selectedCopy.splice(index, 1);
		}
		setSelectedFormTemplateIds([...selectedCopy]);
	}
	function organizeTemplatesByTemplateKey(unorganizedTemplates: FormTemplate[]) {
		const organizedTemplateMap: Record<string, Record<string, FormTemplate[]>> = {};
		for (let i = 0; i < unorganizedTemplates.length; i++) {
			const formTemplate = unorganizedTemplates[i];
			const formTemplateGroup = formTemplate.formTemplateGroup;
			const currentType = formTemplate.template;
			const current = formTemplate;
			if (organizedTemplateMap[formTemplateGroup]) {
				// form template group is already in there
				if (organizedTemplateMap[formTemplateGroup][currentType]) {
					// if it hits this part then the template (category) has been added, so this template just needs
					// to be pushed in the group
					organizedTemplateMap[formTemplateGroup][currentType].push(current);
				} else {
					// it hasnt been added, so ths needs to be initialized with an array with this one template in it.
					organizedTemplateMap[formTemplateGroup][currentType] = [current];
				}
			} else {
				// formTemplateGroup is not in there, so all freshys
				organizedTemplateMap[formTemplateGroup] = {
					[currentType]: [current],
				};
			}
		}

		return organizedTemplateMap;
	}
	function sortTemplateGroups(a: string, b: string) {
		if (a < b) {
			return -1;
		}
		if (a > b) {
			return 1;
		}

		return 0;
	}
	function sortTemplates(a: React.JSX.Element, b: React.JSX.Element) {
		if (a.props.formTemplate.template < b.props.formTemplate.template) {
			return -1;
		}
		if (a.props.formTemplate.template > b.props.formTemplate.template) {
			return 1;
		}

		return 0;
	}

	function determineIfWholeFormTemplateGroupIsSelected(wholeFormTemplateGroup: FormTemplate[]) {
		// basically need to check if selected has all of the wholeFormTemplateGroup selected.
		// so going to set a flag, that will trigger when a form template is not found in selected,
		// which can then break out and will return false;
		let isAllSelected = true;
		// this is gonna be n^2 but these shouldnt get too large, until it does...
		for (let i = 0; i < wholeFormTemplateGroup.length; i++) {
			const formTemplateInGroup = wholeFormTemplateGroup[i];
			let thisOneFound = false;

			for (let ii = 0; ii < selectedFormTemplateIds.length; ii++) {
				const selectedFormTemplate = selectedFormTemplateIds[ii];
				if (formTemplateInGroup.id === selectedFormTemplate) {
					thisOneFound = true;
					continue;
				}
			}

			if (!thisOneFound) {
				isAllSelected = false;
				break;
			}
		}

		return isAllSelected;
	}
	function handleSelectWholeFormTemplateGroup(formTemplatesToAddToSelected: FormTemplate[]) {
		let selectedCopy = [...selectedFormTemplateIds];
		// either remove all or add all depending on if they are in there or not
		const needsRemove = determineIfWholeFormTemplateGroupIsSelected(formTemplatesToAddToSelected);
		if (needsRemove) {
			const formTemplatesToRemoveMap: Record<string, FormTemplate> = {};

			for (let i = 0; i < formTemplatesToAddToSelected.length; i++) {
				const formTemplatesToRemove = formTemplatesToAddToSelected[i];
				formTemplatesToRemoveMap[formTemplatesToRemove.id] = formTemplatesToRemove;
			}

			const newSelected = selectedCopy.filter((id) => {
				if (!formTemplatesToRemoveMap[id]) {
					return id;
				}
			});
			if (setDeselectedFormTemplateIds) setDeselectedFormTemplateIds((prev) => [...prev, ...Object.keys(formTemplatesToRemoveMap)]);

			selectedCopy = newSelected;
		} else {
			const existingIds = new Set(selectedCopy.map((id) => id));
			const uniqueNewTemplates = formTemplatesToAddToSelected.filter((template) => !existingIds.has(template.id));
			selectedCopy = selectedCopy.concat(uniqueNewTemplates.map((template) => template.id));
		}
		setSelectedFormTemplateIds(selectedCopy);
	}
	const listOfTemplates = useMemo(() => {
		const buildList = (templatesToUse: FormTemplate[]) => {
			const offlineTemplatesMap: Record<string, FormTemplateForResolver> = {};

			for (let i = 0; i < offlineTemplates.length; i++) {
				offlineTemplatesMap[offlineTemplates[i].id] = offlineTemplates[i];
			}
			const finalTemplates = templatesToUse;
			let selectedSet: Set<string> = new Set(selectedFormTemplateIds);

			const organizedTemplates = organizeTemplatesByTemplateKey(finalTemplates);
			const listToReturn = [];

			const formTemplateGroups = Object.keys(organizedTemplates).sort(sortTemplateGroups);

			for (let i = 0; i < formTemplateGroups.length; i++) {
				const formTemplateGroup = formTemplateGroups[i];

				const formTemplateGroupObj = organizedTemplates[formTemplateGroup];
				const templateKeys = Object.keys(formTemplateGroupObj);
				let groupedFormTemplates = [];
				let groupedFormTemplatesData = [];
				for (let ii = 0; ii < templateKeys.length; ii++) {
					const currentKey = templateKeys[ii];
					// Need to pick the "default" version of the template.
					let currentTemplate = formTemplateGroupObj[currentKey][0];
					for (let iii = 0; iii < formTemplateGroupObj[currentKey].length; iii++) {
						const potentialDefaultTemplate = formTemplateGroupObj[currentKey][iii];
						if (
							!potentialDefaultTemplate.version ||
							potentialDefaultTemplate.version === "" ||
							(potentialDefaultTemplate.version &&
								(potentialDefaultTemplate.versionName === "default" || potentialDefaultTemplate.versionName === ""))
						) {
							currentTemplate = potentialDefaultTemplate;
						}
					}
					const offline = offlineTemplatesMap[currentTemplate.id] ? true : false;
					const listItem = (
						<TemplateListSyncItem
							key={`template-sync-menu-list-item-${currentTemplate.id}`}
							formTemplate={currentTemplate}
							isOffline={offline}
							isAutoSyncing={false}
							color={colorPalette}
							handleSelect={handleSelect}
							checked={selectedSet.has(currentTemplate.id) ? true : false}
							selectedSet={selectedSet}
							offlineTemplatesMap={offlineTemplatesMap}
							additionalTemplates={formTemplateGroupObj[currentKey]}
							disableAccordion={true}
						/>
					);

					groupedFormTemplates.push(listItem);
					groupedFormTemplatesData.push(currentTemplate);
				}

				const wholeFormTemplateGroupSelected = determineIfWholeFormTemplateGroupIsSelected(groupedFormTemplatesData);
				const sortedTemplates = groupedFormTemplates.sort(sortTemplates);
				let allTemplates: FormTemplate[] = [];
				const valuesArray = Object.values(formTemplateGroupObj);
				for (let j = 0; j < valuesArray.length; j++) {
					const arrayOfThings = valuesArray[j];
					allTemplates = allTemplates.concat(arrayOfThings);
				}
				const first = 0;
				const last = formTemplateGroups.length;

				const groupedHtml = (
					<Accordion.Container
						style={{
							borderBottom: `1px solid ${colorPalette.border.primary}`,
							borderTop: i === first || i === last ? `1px solid ${colorPalette.border.primary}` : "none",
							width: "100%",
						}}
						key={`template-list-${formTemplateGroup}`}>
						<Accordion.Item
							value="template-key"
							style={{ color: colorPalette.text.primary, background: colorPalette.background.primary, borderRadius: "0px" }}>
							<Accordion.Trigger style={{ height: "40px" }}>
								<div style={{ display: "flex", justifyContent: "start", width: "100%", alignItems: "center", gap: "10px" }}>
									<input
										className={styles.form_template_group_check}
										type="checkbox"
										id={`select-all-${formTemplateGroup}`}
										onClick={(e) => e.stopPropagation()}
										checked={wholeFormTemplateGroupSelected}
										onChange={() => handleSelectWholeFormTemplateGroup(allTemplates)}
									/>
									<div style={{ fontWeight: "700" }}>{formTemplateGroup}</div>
								</div>
								<div style={{ display: "flex", justifyContent: "space-between", fontWeight: "600", gap: "10px" }}>
									<div
										style={{
											display: "flex",
											background: colorPalette.background.tertiary,
											color: colorPalette.text.badge,
											borderRadius: "32px",
											width: "12px",
											height: "8px",
											justifyContent: "center",
											alignItems: "center",
											padding: "8px",
										}}>
										{sortedTemplates.length}
									</div>
									<Accordion.Icon style={{ display: "flex" }} />
								</div>
							</Accordion.Trigger>
							<Accordion.Content style={{ background: colorPalette.background.tertiary }}>{sortedTemplates}</Accordion.Content>
						</Accordion.Item>
					</Accordion.Container>
				);

				listToReturn.push(groupedHtml);
			}

			return listToReturn;
		};
		return <>{formTemplates.length > 0 ? buildList(formTemplates) : <div>Loading...</div>}</>;
	}, [formTemplates, selectedFormTemplateIds, offlineTemplates, colorPalette]);

	return (
		<>
			{editSubmit ? (
				<Modal colorPalette={colorPalette} open={openSelectFormTemplates} setOpen={setOpenSelectFormTemplates}>
					<DialogContainer>
						<ModalTitleContainer>
							<ModalTitle>Select Templates</ModalTitle>
							<FiX onClick={handleClose} size="24" />
						</ModalTitleContainer>
						<ModelSubHeading>Choose the templates you'll need for this project. (You can only add templates while you are online)</ModelSubHeading>
						<div style={{ display: "flex", flexDirection: "column", overflow: "auto", maxHeight: "40vh", width: "100%" }}>{listOfTemplates}</div>
						<ModelSubHeading>If you need a form template not listed here, please contact us using the report a bug button.</ModelSubHeading>
						<ModalFooter>
							<Button
								type="button"
								data-test={"back-to-edit-project"}
								colorPalette={colorPalette}
								variant="outlined"
								label="Previous"
								onClick={() => setOpenSelectFormTemplates(!openSelectFormTemplates)}
							/>
							{}
							<Button
								type="submit"
								data-test={"create-project-submit"}
								colorPalette={colorPalette}
								onClick={editSubmit}
								variant="filled"
								label="Done"
							/>
						</ModalFooter>
					</DialogContainer>
				</Modal>
			) : (
				<Modal colorPalette={colorPalette} open={openSelectFormTemplates} setOpen={setOpenSelectFormTemplates}>
					<Form>
						<DialogContainer>
							<ModalTitleContainer>
								<ModalTitle>Select Templates</ModalTitle>
								<FiX onClick={handleClose} size="24" />
							</ModalTitleContainer>
							<ModelSubHeading>
								Choose the templates you'll need for this project. (You can only add templates while you are online)
							</ModelSubHeading>
							<div style={{ display: "flex", flexDirection: "column", overflow: "auto", maxHeight: "40vh", width: "100%" }}>
								{listOfTemplates}
							</div>
							<ModelSubHeading>If you need a form template not listed here, please contact us using the report a bug button.</ModelSubHeading>
							<ModalFooter>
								<Button
									type="button"
									data-test={"back-to-edit-project"}
									colorPalette={colorPalette}
									variant="outlined"
									label="Previous"
									onClick={() => setOpenSelectFormTemplates(!openSelectFormTemplates)}
								/>
								<Button
									type="submit"
									disabled={isSubmitting}
									data-test={"create-project-submit"}
									colorPalette={colorPalette}
									variant="filled"
									label="Done"
								/>
							</ModalFooter>
						</DialogContainer>
					</Form>
				</Modal>
			)}
		</>
	);
};
